import { useAuth } from "@/context/AuthContext";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, ArrowLeftRight, Target, Wallet, Repeat, Sparkles, LogOut, PiggyBank, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, testId: "nav-transactions" },
  { to: "/budgets", label: "Budgets", icon: PiggyBank, testId: "nav-budgets" },
  { to: "/objectifs", label: "Objectifs", icon: Target, testId: "nav-goals" },
  { to: "/comptes", label: "Comptes", icon: Wallet, testId: "nav-accounts" },
  { to: "/recurrent", label: "Récurrent", icon: Repeat, testId: "nav-recurring" },
  { to: "/import", label: "Import", icon: Upload, testId: "nav-import" },
  { to: "/conseils", label: "Conseils IA", icon: Sparkles, testId: "nav-ai" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed top-0 left-0 h-screen w-64 bg-white border-r border-stone-200 flex-col z-30">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-stone-900 rounded-xl flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">Budgeo</div>
              <div className="text-xs text-stone-500 mt-0.5">Gestion budgétaire</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-stone-900 text-white"
                    : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold text-sm">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-stone-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-stone-600 hover:text-stone-900"
            onClick={doLogout}
            data-testid="btn-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="md:ml-64 pb-24 md:pb-8 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-xl bg-white/90 border-t border-stone-200/60 z-40">
        <div className="grid grid-cols-5 h-16">
          {[NAV[0], NAV[1], NAV[2], NAV[6], { to: "/profil", label: "Profil", icon: LogOut, testId: "nav-profile" }].map(
            ({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={testId}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors ${
                    isActive ? "text-stone-900" : "text-stone-400"
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            )
          )}
        </div>
      </nav>
    </div>
  );
}
